import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CommissionWithRelations,
  CreateCommissionInput,
  UpdateCommissionInput,
  IncomeReport,
} from '@peles/shared';
import * as commissionsApi from '../api/commissions';

export function useCommissions(filters?: {
  vendorId?: string;
  eventId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery<CommissionWithRelations[]>({
    queryKey: ['commissions', filters ?? 'all'],
    queryFn: () => commissionsApi.getCommissions(filters),
  });
}

export function useCreateCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCommissionInput) => commissionsApi.createCommission(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateCommissionInput }) =>
      commissionsApi.updateCommission(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionsApi.deleteCommission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useIncomeReport(filters?: { startDate?: string; endDate?: string }) {
  return useQuery<IncomeReport>({
    queryKey: ['reports', 'income', filters],
    queryFn: () => commissionsApi.getIncomeReport(filters),
    enabled: true,
  });
}
